import React, { useContext, useEffect, useMemo, useRef } from "react";
import { getPeriodKey } from "@common/helpers/reports";
import { ExpenseByVendorResponse } from "@common/types/reports/types";
import {
    ExpenseByVendorSort,
    sortExpenseByVendorRows,
} from "@common/helpers/expenseByVendorSort";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { AccountingReportContext } from "../AccountingReport.context";
import { GlTableCallbacks, GlTableMeta } from "../glDrillDown.lib";
import { hasSingleValueColumn } from "../singleColumnReport";
import { buildExpenseByVendorRows } from "./expenseByVendorViewBuilder";
import {
    buildExpenseByVendorConfig,
    buildExpenseByVendorView,
} from "./expenseByVendorViewConfig";
import styles from "./ExpenseByVendorTable.module.scss";

interface Props {
    data: ExpenseByVendorResponse;
    sort: ExpenseByVendorSort;
    onSortChange: (sort: ExpenseByVendorSort) => void;
    onDrillDown: GlTableCallbacks["onDrillDown"];
}

/**
 * Expense by Vendor table rendered via the shared ViewTable runtime.
 * - Columns are built dynamically from data.periods (ephemeral, never persisted)
 * - Total-period columns are pinned right via view.columns[i].options.pin
 * - Flat data; no nesting / expand-collapse
 * - Sort is owned by the page; rows are pre-sorted before being handed to
 *   ViewTable, since ViewTable's `sort` / `onSortChange` props only drive the
 *   `SortableHeader` caret state — not the actual row order.
 */
export const ExpenseByVendorTable: React.FC<Props> = ({
    data,
    sort,
    onSortChange,
    onDrillDown,
}) => {
    const viewTableRef = useRef<ViewTableHandle>(null);
    const { numberFormat } = useContext(AccountingReportContext);

    const rows = useMemo(() => {
        const sortedReport: ExpenseByVendorResponse = {
            ...data,
            rows: sortExpenseByVendorRows(data.rows, sort),
        };
        return buildExpenseByVendorRows(sortedReport);
    }, [data, sort]);

    const view = useMemo(
        () => buildExpenseByVendorView(data.periods),
        [data.periods],
    );

    const tableMeta: GlTableMeta = useMemo(
        () => ({ callbacks: { onDrillDown } }),
        [onDrillDown],
    );
    const config = useMemo(
        () => buildExpenseByVendorConfig(data.periods, numberFormat),
        [data.periods, numberFormat],
    );

    // Derive a key from the period identity so ViewTable remounts when entity
    // or date range changes.  Sort changes don't affect the key (the same
    // periods stay in place; only the row order changes).
    const reportKey = useMemo(
        () => data.periods.map(getPeriodKey).join("|"),
        [data.periods],
    );

    // Scroll to the rightmost column when the report changes so the most
    // recent period (or the total column) is visible on load.
    useEffect(() => {
        const timerId = window.setTimeout(() => {
            viewTableRef.current?.scrollTo({ left: Number.MAX_SAFE_INTEGER });
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [reportKey]);

    return (
        <ViewTable
            key={reportKey}
            ref={viewTableRef}
            data={rows}
            view={view}
            config={config}
            sort={sort}
            onSortChange={onSortChange}
            tableMeta={tableMeta}
            className={styles.expenseByVendorTable}
            centerColumns={hasSingleValueColumn(data.periods)}
            rowBorders={false}
            enableColumnResizing
        />
    );
};
